import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Button,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import NotificationsIcon from "@material-ui/icons/Notifications";
import CachedIcon from "@material-ui/icons/Cached";
import api from "../services/api";
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BirthdayModal from "../components/BirthdayModal";
import ChatPopover from "../pages/Chat/ChatPopover";
import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import VersionControl from "../components/VersionControl";
import useSocketListener from "../hooks/useSocketListener";
import { FaGlobe } from "react-icons/fa";

const backendUrl = getBackendUrl();
const drawerWidth = 256;


const useStyles = makeStyles((theme) => ({
  root: { display: "flex", height: "100vh", "@supports (height: 100dvh)": { height: "100dvh" }, background: theme.palette.background.default },
  toolbar: { minHeight: 64, gap: 4, padding: "0 20px", color: theme.palette.text.secondary, background: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}`, "& > .MuiIconButton-root": { color: "inherit" }, [theme.breakpoints.down("xs")]: { padding: "0 8px", gap: 0 } },
  toolbarIcon: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", height: 80, gap: 8, flexShrink: 0, color: "#a9bad3", borderBottom: "1px solid #263449", "& .MuiIconButton-root": { color: "inherit", padding: 8, flexShrink: 0 } },
  appBar: { zIndex: theme.zIndex.drawer, width: "calc(100% - 72px)", marginLeft: 72, background: theme.palette.background.paper, transition: theme.transitions.create(["width", "margin"]), [theme.breakpoints.down("xs")]: { width: "100%", marginLeft: 0 } },
  appBarShift: { marginLeft: drawerWidth, width: `calc(100% - ${drawerWidth}px)`, [theme.breakpoints.down("xs")]: { marginLeft: 0, width: "100%" } },
  fullAppBar: { width: "100%", marginLeft: 0 },
  menuButtonHidden: { display: "none" },
  title: { flex: 1, minWidth: 0, color: theme.palette.text.primary, fontSize: 15, fontWeight: 600 },
  subtitle: { display: "block", fontSize: 11, color: theme.palette.text.secondary, fontWeight: 400, marginTop: 2 },
  drawerShell: { flexShrink: 0, width: drawerWidth, [theme.breakpoints.down("xs")]: { width: 0 } },
  drawerShellClosed: { width: 72, [theme.breakpoints.down("xs")]: { width: 0 } },
  drawerPaper: { [theme.breakpoints.down("xs")]: { position: "fixed" }, position: "relative", whiteSpace: "nowrap", width: drawerWidth, background: "#111e32", color: "#adbbd0", border: 0, transition: theme.transitions.create("width"), overflowX: "hidden", "& .MuiDivider-root": { background: "#263449" }, "& .MuiListSubheader-root": { background: "transparent", color: "#7f93b0", fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", lineHeight: "36px" } },
  drawerPaperClose: { width: 72, overflowX: "hidden", "& .MuiListItemText-root, & .MuiListSubheader-root": { display: "none" }, "& .MuiListItem-root": { paddingLeft: 10, paddingRight: 10 }, "& .MuiListItemIcon-root": { minWidth: 36 }, "& .MuiListItem-root > .MuiSvgIcon-root": { display: "none" } },
  appBarSpacer: { height: 64, flexShrink: 0 },
  content: { flex: 1, minWidth: 0, height: "100%", overflow: "auto" },
  containerWithScroll: { flex: 1, overflowY: "auto", overflowX: "hidden", padding: "14px 10px", ...theme.scrollbarStylesSoft },
  logo: { display: "block", width: 184, maxWidth: "calc(100% - 40px)", height: 56, padding: "6px 8px", boxSizing: "border-box", flexShrink: 0, objectFit: "contain", objectPosition: "center", background: "#ffffff", borderRadius: 8 },
  logoCompact: { width: 40, maxWidth: "none", height: 40, padding: 3 },
  workspace: { padding: "16px 20px", borderTop: "1px solid #263449", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", color: "#9eb0cc", flexShrink: 0 },
  avatar2: { width: 34, height: 34, border: `1px solid ${theme.palette.divider}`, fontSize: 13, background: theme.palette.primary.main, color: theme.palette.primary.contrastText },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const LoggedInLayout = ({ children, themeToggle, hideMenu = false }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const initializedDrawerUser = useRef(null);

  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);

  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const pageKey = location.pathname.split("/")[1];
  const pageLabels = { tickets: "Central de atendimento", contacts: "Relacionamento", users: "Equipe", connections: "Canais de comunicação", settings: "Configurações", kanban: "Pipeline", reports: "Relatórios", schedules: "Agenda", "quick-messages": "Respostas rápidas", tags: "Etiquetas", chats: "Colaboração", campaigns: "Campanhas", financeiro: "Financeiro", whitelabel: user.resellerId ? "Minha revenda" : "Revendedores e clientes" };
  const pageTitle = pageLabels[pageKey] || (pageKey ? "Área de trabalho" : "Visão geral");
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);


  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: {
            status: true,
            pageNumber: "1"
          }
        });

        // Filtra apenas os informativos ativos e não expirados
        const activeAnnouncements = (data.records || []).filter(announcement => {
          const isActive = announcement.status === true || announcement.status === "true";
          const isNotExpired = !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
          return isActive && isNotExpired;
        });

        setAnnouncements(activeAnnouncements);

        // Mostra o modal apenas se houver informativos ativos
        if (activeAnnouncements.length > 0) {
          setShowAnnouncementsModal(true);
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (user?.id) {
      fetchAnnouncements();
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || initializedDrawerUser.current === user.id) return;
    initializedDrawerUser.current = user.id;
    setDrawerOpen(!isMobile && user.defaultMenu !== "closed");
  }, [isMobile, user?.id, user.defaultMenu]);

  useEffect(() => {
    const companyId = user?.companyId;

    if (companyId) {
      const buildProfileUrl = () => {
        const savedProfileImage = localStorage.getItem("profileImage");
        const currentProfileImage = user.profileImage || (savedProfileImage !== "null" && savedProfileImage !== "undefined" ? savedProfileImage : null);

        if (currentProfileImage) {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return null;
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage]);

  // Callbacks dos eventos
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Sua conta foi acessada em outro computador.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id]);

  // Callbacks para eventos de aniversário
  const handleUserBirthday = useCallback((data) => {
    console.log("🎂 Evento de aniversário de usuário recebido:", data);
    if (data.userId === +user?.id) {
      setShowBirthdayModal(true);
    }
  }, [user?.id]);

  const handleContactBirthday = useCallback((data) => {
    console.log("🎂 Evento de aniversário de contato recebido:", data);
    // Para contatos, apenas logamos por enquanto
    // A mensagem já foi enviada automaticamente pelo backend
  }, []);

  // Verificar aniversários no login
  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const birthdayData = data.data;

        // Verificar se o usuário atual faz aniversário hoje
        const userBirthday = birthdayData.users.find(u => u.id === +user.id);
        if (userBirthday) {
          console.log("🎂 Usuário faz aniversário hoje! Mostrando modal...");
          setShowBirthdayModal(true);
        }

        // Se há aniversariantes, mostrar notificação
        if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
          console.log("🎂 Há aniversariantes hoje:", birthdayData);
        }
      } catch (error) {
        console.error("Erro ao verificar aniversários:", error);
      }
    }
  }, [user?.id, user?.companyId]);

  // Registrar listeners
  useSocketListener(socket, user, 'auth', handleAuthEvent);
  useSocketListener(socket, user, 'user', handleUserUpdate);
  useSocketListener(socket, user, 'user-birthday', handleUserBirthday);
  useSocketListener(socket, user, 'contact-birthday', handleContactBirthday);

  // Verificar aniversários quando o usuário faz login
  useEffect(() => {
    if (user?.id && user?.companyId) {
      // Pequeno delay para garantir que o socket esteja conectado
      const timer = setTimeout(() => {
        checkBirthdaysOnLogin();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleLanguageChange = (lng) => {
    setLanguageAnchor(null);
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "ar", label: "عربي" },
  ];

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const { getAll } = useSettings();
  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) {
            langs = JSON.parse(enabledLanguagesSetting);
          }
        } catch { }
        console.log(
          "Layout - enabledLanguages carregadas:",
          langs,
          "para companyId:",
          user?.companyId
        );
        setEnabledLanguages(Array.isArray(langs) ? langs : ["pt-BR", "en"]);
      } catch (error) {
        console.log("Layout - erro ao carregar enabledLanguages:", error);
      }
    }
    if (user?.companyId) fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  if (loading || updateInProgress) {
    return <BackdropLoading />;
  }

  return (
    <div className={clsx(classes.root, "logged-in-layout")}>
      {!hideMenu && (
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          ModalProps={{ keepMounted: true, disableEscapeKeyDown: true }}
          className={clsx(classes.drawerShell, !drawerOpen && classes.drawerShellClosed)}
          classes={{
            paper: clsx(
              classes.drawerPaper,
              !drawerOpen && classes.drawerPaperClose
            ),
          }}
          open={drawerOpen}
        >
          <div className={classes.toolbarIcon}>
            <img className={clsx(classes.logo, "enterprise-sidebar-logo", !drawerOpen && classes.logoCompact)} src={theme.calculatedLogoLight()} alt={theme.appName} />
            {drawerOpen && <IconButton aria-label="Recolher navegação" onClick={() => setDrawerOpen(false)}><ChevronLeftIcon fontSize="small" /></IconButton>}
          </div>
          <List className={clsx(classes.containerWithScroll, "enterprise-sidebar")}>
            {/* {mainListItems} */}
            <MainListItems collapsed={!drawerOpen} />
          </List>
          {drawerOpen && <div className={classes.workspace}>{user?.company?.name || theme.appName}<br /><span style={{ fontSize: "0.625rem", color: "#7f93b0" }}>Ambiente de trabalho</span></div>}
        </Drawer>
      )}

      <AppBar
        position="absolute"
        className={clsx(classes.appBar, hideMenu && classes.fullAppBar, !hideMenu && drawerOpen && classes.appBarShift)}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          {!hideMenu && (
            <IconButton
              edge="start"
              variant="contained"
              aria-label="Abrir navegação"
              style={{ color: "inherit" }}
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={clsx(drawerOpen && classes.menuButtonHidden)}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            component="h2"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
          >
            {isMobile && pageKey === "tickets" ? "Atendimentos" : pageTitle}
            <span className={classes.subtitle}>{user?.company?.name || theme.appName}</span>
          </Typography>

          {!hideMenu && (
            <>
              {greaterThenSm && <VersionControl onUpdateStart={handleUpdateStart} onUpdateComplete={handleUpdateComplete} />}
              <IconButton aria-label="Selecionar idioma" onClick={(event) => setLanguageAnchor(event.currentTarget)}><FaGlobe size={18} /></IconButton>
              <Menu anchorEl={languageAnchor} open={Boolean(languageAnchor)} onClose={() => setLanguageAnchor(null)} getContentAnchorEl={null} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                {filteredLanguageOptions.map((lang) => <MenuItem key={lang.code} selected={i18n.language === lang.code} onClick={() => handleLanguageChange(lang.code)}>{lang.label}</MenuItem>)}
              </Menu>

              <IconButton aria-label={theme.mode === "dark" ? "Ativar tema claro" : "Ativar tema escuro"} onClick={colorMode.toggleColorMode}>
                {theme.mode === "dark" ? (
                  <Brightness7Icon style={{ color: "inherit" }} />
                ) : (
                  <Brightness4Icon style={{ color: "inherit" }} />
                )}
              </IconButton>

              {greaterThenSm && <NotificationsVolume setVolume={setVolume} volume={volume} />}

              {greaterThenSm && <IconButton
                onClick={handleRefreshPage}
                aria-label={i18n.t("mainDrawer.appBar.refresh")}
                color="inherit"
              >
                <CachedIcon style={{ color: "inherit" }} />
              </IconButton>}

              {/* <DarkMode themeToggle={themeToggle} /> */}

              {user.id && <NotificationsPopOver volume={volume} />}

              {greaterThenSm && <AnnouncementsPopover />}

              {greaterThenSm && <ChatPopover />}



              <div className="user-menu-wrapper">
                <IconButton aria-label="Abrir menu do usuário" onClick={handleMenu} style={{ padding: "0.25rem", marginLeft: "0.5rem" }}><StyledBadge
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  variant="dot"
                >
                  <Avatar
                    alt={user.name}
                    className={classes.avatar2}
                    src={profileUrl}
                  >{user?.name?.charAt(0)?.toUpperCase()}</Avatar>
                </StyledBadge></IconButton>

                <UserModal
                  open={userModalOpen}
                  onClose={() => setUserModalOpen(false)}
                  onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
                  userId={user?.id}
                />

                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={menuOpen}
                  onClose={handleCloseMenu}
                  PaperProps={{
                    style: {
                      minWidth: "9.375rem",
                      maxWidth: "12.5rem",
                      width: "auto",
                    },
                  }}
                >
                  <MenuItem onClick={handleOpenUserModal}>
                    {i18n.t("mainDrawer.appBar.user.profile")}
                  </MenuItem>
                  {!greaterThenSm && <MenuItem onClick={() => { handleCloseMenu(); setShowAnnouncementsModal(true); }}>Informativos</MenuItem>}
                  <MenuItem onClick={handleClickLogout}>
                    {i18n.t("mainDrawer.appBar.user.logout")}
                  </MenuItem>
                </Menu>
              </div>
            </>
          )}
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {selectedAnnouncement.text}
              </Typography>
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: "1rem" }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              )}
              <Button
                onClick={() => setSelectedAnnouncement(null)}
                style={{ marginTop: "1rem" }}
                variant="outlined"
              >
                Voltar para lista
              </Button>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Prioridade: {announcement.priority === 1 ? 'Alta' : announcement.priority === 2 ? 'Média' : 'Baixa'}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAnnouncementsModal(false)}
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário */}
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />

    </div>
  );
};

export default LoggedInLayout;